import React, { useRef, useEffect, useState } from 'react';
import { rgba } from 'polished';
import styled, { useTheme } from 'styled-components';
import { Link } from 'gatsby';
import { Logo, Anchor, Flex } from '../../../components/blocks';

const Root = styled.header`
  position: sticky;
  z-index: 20;
  top: 0;
  width: 100%;

  &.open {
    ${({ theme }) =>
      theme.screen.tablet({
        position: 'fixed',
      })}
  }
`;

const Wrap = styled.div`
  display: grid;
  grid-template-columns: min-content 1fr;
  grid-template-areas: 'logo menu';
  align-items: center;
  justify-content: space-between;
  column-gap: ${({ theme }) => theme.spaces.l};
  width: 100%;
  padding: ${({ theme }) => `${theme.spaces.m} ${theme.spaces.l}`};
  background: ${({ theme }) => rgba(theme.backgroundColors.white, 0.8)};
  backdrop-filter: blur(4px);

  ${({ theme }) =>
    theme.screen.tablet({
      gridTemplateColumns: '1fr',
      gridTemplateRows: 'auto',
      gridTemplateAreas: `"logo ham" "menu menu"`,
      gap: theme.spaces.s,
    })}
`;

const LogoWrap = styled(Link)`
  display: flex;
  margin: 0;
  padding: 0;
  grid-area: logo;
`;

const Menu = styled.div`
  display: flex;
  align-items: center;
  grid-area: menu;

  ${({ theme }) =>
    theme.screen.tablet({
      minHeight: '100vh',
      height: '100%',
      alignItems: 'flex-start',
      flexDirection: 'column',
      display: 'none',
    })}

  &.open {
    ${({ theme }) =>
      theme.screen.tablet({
        display: 'flex',
      })}
  }
`;

const HambergerMenu = styled.div`
  grid-aream: ham;
  position: relative;
  display: none;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  width: 40px;
  height: 40px;
  padding: ${({ theme }) => theme.spaces.s} 12px;
  border-radius: ${({ theme }) => theme.radii.circle};
  row-gap: ${({ theme }) => theme.spaces.s};
  flex-shrink: 0;
  transition-duration: ${({ theme }) => theme.transitions.normal};
  user-select: none;

  &:hover {
    cursor: pointer;
    background-color: ${({ theme }) =>
      rgba(theme.backgroundColors.lightgrey, 0.2)};
  }

  ${({ theme }) =>
    theme.screen.tablet({
      display: 'flex',
    })}

  &:before, &:after {
    content: '';
    display: block;
    width: 100%;
    height: 2px;
    background: ${({ theme }) => theme.backgroundColors.black};
    transition-duration: ${({ theme }) => theme.transitions.normal};
  }

  .open & {
    &:before {
      transform: rotate(-45deg) translate(-2px, 2px);
    }

    &:after {
      transform: rotate(45deg) translate(-2px, -2px);
    }
  }
`;

const Nav = styled.ul`
  display: flex;
  margin: 0 0 0 auto;
  padding: 0;
  list-style-type: none;
  align-items: center;

  ${({ theme }) =>
    theme.screen.tablet({
      width: '100%',
      margin: `0 auto 0 0`,
      flexDirection: 'column',
      alignItems: 'flex-start',
    })}
`;

const NavItem = styled.li`
  margin: 0;
  padding: 0;

  ${({ theme }) =>
    theme.screen.tablet({
      width: '100%',
    })}
`;

const NavLink = styled(Link)`
  display: block;
  font-size: ${({ theme }) => theme.fontSizes.s};
  padding: ${({ theme }) => theme.spaces.l};
  transition-duration: ${({ theme }) => theme.transitions.normal};
  user-select: none;

  ${({ theme }) =>
    theme.screen.tablet({
      width: '100%',
      padding: `${theme.spaces.xl} ${theme.spaces.m}`,
      whiteSpace: 'nowrap',
    })}

  &:hover,
  &.active {
    box-shadow: 0 2px ${({ theme }) => theme.backgroundColors.blue};

    ${({ theme }) =>
      theme.screen.tablet({
        boxShadow: 'none',
        backgroundColor: theme.backgroundColors.primary,
        color: theme.fontColors.white,
      })}
  }
`;

const NavButton = styled.span<{ expand?: boolean }>`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  column-gap: 4px;
  font-size: ${({ theme }) => theme.fontSizes.s};
  padding: ${({ theme }) => theme.spaces.l};
  transition-duration: ${({ theme }) => theme.transitions.normal};
  user-select: none;

  ${({ theme }) =>
    theme.screen.tablet({
      padding: `${theme.spaces.xl} ${theme.spaces.m}`,
      whiteSpace: 'nowrap',
    })}

  &:after {
    content: '';
    display: block;
    width: 8px;
    height: 8px;
    border: 1px solid transparent;
    border-top-color: ${({ theme }) => theme.backgroundColors.black};
    border-left-color: ${({ theme }) => theme.backgroundColors.black};
    transition: ${({ theme }) => theme.transitions.normal};
    transform: ${({ expand }) => (expand ? 'rotate(45deg)' : 'rotate(225deg)')};
    margin-bottom: ${({ theme, expand }) =>
      expand ? `-${theme.spaces.s}` : theme.spaces.s};
  }

  &:hover,
  &.active {
    color: ${({ theme }) => theme.fontColors.blue};

    ${({ theme }) =>
      theme.screen.tablet({
        boxShadow: 'none',
        color: theme.fontColors.primary,
        fontWeight: theme.fontWeights.bold,
      })}

    &:after {
      margin-bottom: -2px;
      border-top-color: ${({ theme }) => theme.backgroundColors.primary};
      border-left-color: ${({ theme }) => theme.backgroundColors.primary};

      ${({ theme }) =>
        theme.screen.tablet({
          borderTopColor: theme.backgroundColors.primary,
        })};
    }
  }
`;

const SPInquiry = styled(Anchor)`
  display: none;

  ${({ theme }) =>
    theme.screen.tablet({
      display: 'block',
    })}
`;

const ChildNav = styled.ul`
  position: absolute;
  display: flex;
  flex-direction: column;
  margin: 0 0 0 auto;
  padding: 0;
  list-style-type: none;
  background-color: ${({ theme }) => theme.backgroundColors.white};
  box-shadow: ${({ theme }) => theme.boxShadow.nav};
  border-radius: ${({ theme }) => theme.radii.l};
  outline: 0;

  ${({ theme }) =>
    theme.screen.tablet({
      position: 'relative',
      border: `1px solid ${theme.borderColors.default}`,
      borderRadius: theme.radii.m,
      backgroundColor: theme.backgroundColors.default,
      boxShadow: 'none',
    })}

  ${NavItem} {
    &:first-child {
      border-radius: ${({ theme }) => `${theme.radii.l} ${theme.radii.l} 0 0`};
      overflow: hidden;

      ${({ theme }) =>
        theme.screen.tablet({
          borderRadius: `${theme.radii.m} ${theme.radii.m} 0 0`,
        })}
    }

    &:last-child {
      border-radius: ${({ theme }) => `0 0 ${theme.radii.l} ${theme.radii.l}`};
      overflow: hidden;

      ${({ theme }) =>
        theme.screen.tablet({
          borderRadius: `0 0 ${theme.radii.m} ${theme.radii.m}`,
        })}
    }
  }

  ${NavLink} {
    padding: ${({ theme }) => theme.spaces.l};

    display: flex;
    align-items: center;
    column-gap: ${({ theme }) => theme.spaces.m};

    &:before {
      content: '';
      display: flex;
      align-items: center;
      width: 8px;
      height: 2px;
      background-color: ${({ theme }) => theme.borderColors.default};
    }

    &:hover,
    &.active {
      background: ${({ theme }) => theme.backgroundColors.primary};
      color: ${({ theme }) => theme.fontColors.white};
      box-shadow: none;
    }
  }
`;

const InquiryButton = styled(Anchor)`
  ${({ theme }) =>
    theme.screen.tablet({
      marginTop: theme.spaces.xl,
      width: '100%',
    })}
`;

const InquiryButtonWrap = styled(Flex)`
  gap: ${({ theme }) => theme.spaces.m};

  ${({ theme }) =>
    theme.screen.tablet({
      flexDirection: 'column',
      gap: 0,
      width: '100%',
    })}
`;

export const Header: React.FC = () => {
  const theme = useTheme();
  /* TODO: refactoring */
  const [isOpen_1, setIsOpen_1] = useState(false);
  const [isOpen_2, setIsOpen_2] = useState(false);
  const menuRef_1: any = useRef();
  const menuRef_2: any = useRef();
  const [spOpen, setSpOpen] = useState(false);
  const spRef: any = useRef();

  useEffect(() => {
    isOpen_1 && menuRef_1.current.focus();
  }, [isOpen_1]);
  useEffect(() => {
    isOpen_2 && menuRef_2.current.focus();
  }, [isOpen_2]);
  useEffect(() => {
    spOpen && spRef.current.focus();
  }, [spOpen]);

  return (
    <Root className={spOpen && 'open'}>
      <Wrap>
        <LogoWrap to="/">
          <Logo color="default" width="130" />
        </LogoWrap>
        <Menu ref={spRef} className={spOpen && 'open'}>
          <Nav>
            <NavItem>
              <NavButton
                onClick={() => setIsOpen_1(isOpen_1 ? false : true)}
                expand={isOpen_1}
              >
                Dentisとは
              </NavButton>
              {isOpen_1 && (
                <ChildNav
                  ref={menuRef_1}
                  tabIndex={1}
                  onBlur={() => setTimeout(() => setIsOpen_1(false), 200)}
                >
                  <NavItem>
                    <NavLink activeClassName="active" to="/features">
                      Dentisの特長
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink activeClassName="active" to="/benefits">
                      歯科DXの業務
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink activeClassName="active" to="/function">
                      機能一覧
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink activeClassName="active" to="/security">
                      セキュリティの取り組み
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink activeClassName="active" to="/support">
                      充実のサポート
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink activeClassName="active" to="/vision">
                      Dentisの想い
                    </NavLink>
                  </NavItem>
                </ChildNav>
              )}
            </NavItem>
            <NavItem>
              <NavButton
                onClick={() => setIsOpen_2(isOpen_2 ? false : true)}
                expand={isOpen_2}
              >
                便利な機能
              </NavButton>
              {isOpen_2 && (
                <ChildNav
                  ref={menuRef_2}
                  tabIndex={2}
                  onBlur={() => setTimeout(() => setIsOpen_2(false), 200)}
                >
                  <NavItem>
                    <NavLink activeClassName="active" to="/kakaritsuke">
                      予約システム
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink activeClassName="active" to="/online">
                      オンライン診療
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink activeClassName="active" to="/questionnaire">
                      Web問診
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink activeClassName="active" to="/message">
                      メッセージ配信
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink activeClassName="active" to="/visit">
                      訪問歯科
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink activeClassName="active" to="/subkarte">
                      サブカルテ
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink activeClassName="active" to="/karte">
                      カルテ・レセコン
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink activeClassName="active" to="/analytics">
                      分析・物販
                    </NavLink>
                  </NavItem>
                </ChildNav>
              )}
            </NavItem>
            <NavItem>
              <NavLink activeClassName="active" to="/case">
                導入事例
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink activeClassName="active" to="/plan">
                料金
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                activeClassName="active"
                partiallyActive={true}
                to="/news"
              >
                お知らせ・イベント
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                activeClassName="active"
                partiallyActive={true}
                to="/blog/"
              >
                ブログ
              </NavLink>
            </NavItem>
          </Nav>
          <InquiryButtonWrap>
            <InquiryButton
              radius="rounded"
              color="secondary"
              size="s"
              wide="head"
              to="/form/inquiry"
            >
              お問い合わせ
            </InquiryButton>
            <InquiryButton
              radius="rounded"
              color="primary"
              size="s"
              wide="head"
              to="/resource"
            >
              お役立ち資料
            </InquiryButton>
          </InquiryButtonWrap>
        </Menu>
        <Flex ml="auto" alignItems="center" gap={theme.spaces.m}>
          <SPInquiry
            radius="rounded"
            color="secondary"
            size="s"
            wide="head"
            to="/form/inquiry"
          >
            お問い合わせ
          </SPInquiry>
          <HambergerMenu onClick={() => setSpOpen(spOpen ? false : true)} />
        </Flex>
      </Wrap>
    </Root>
  );
};
